/**
 * API Middleware
 *
 * Centralized middleware for handling all API requests.
 * Automatically routes requests based on environment:
 * - Local environment: Uses REST API via apiClient (axios)
 * - Web/Embedded environment: Uses parent window postMessage via getAPIGatewayDataFromParent
 *
 * This provides a consistent interface for all API calls and normalizes responses.
 */

import { apiClient } from './apiClient';
import { getAPIGatewayDataFromParent } from './boson/bosonClient';
import { isLocalEnvironment } from '../utils/environmentDetection';
import {
  mockAssociateImageQuality,
  mockEvidencePhotoLinks,
  mockImageQualityReport,
  mockImageQualitySiteRankings,
} from '../devDataProviders/imageQualityMocks';
import { MockDataService } from '../devDataProviders/mockDataService';
import { getDefaultNodeId, getDefaultWarehouseId } from './devInitializer';

/**
 * Enhanced error processing for ValidationFailedException
 * 
 * @param error - The original error object
 * @returns Enhanced error object if ValidationFailedException, otherwise original error
 */
function enhanceValidationError(error: any): any {
  // Only process ValidationFailedException
  const errorResponse = error.errorResponse;
  if (errorResponse && errorResponse.statusCode === "ValidationFailedException" && errorResponse.errorMessage) {
    try {
      const parsedError = JSON.parse(errorResponse.errorMessage);
      if (parsedError.message) {
        // Return enhanced error with extracted message
        return {
          ...error,
          message: parsedError.message
        };
      }
    } catch (parseError) {
      // If parsing fails, return original error unchanged
    }
  }
  // Return original error unchanged for all other cases
  return error;
}

/**
 * API request payload structure
 */
export interface ApiRequestPayload {
  /** The API endpoint path (e.g., "/createShiftPlan") */
  resourcePath: string;

  /** HTTP method for the request */
  httpMethod: string;

  /** Process name (only used for parent window proxy, ignored in local REST calls) */
  processName?: string;

  /** Request body/payload */
  requestBody?: any;

  /** Optional request timeout in milliseconds */
  timeout?: number;
}

/**
 * Normalized API response structure
 */
export interface ApiResponse<T = any> {
  /** Response data */
  data?: T;

  /** HTTP status code */
  status?: number;

  /** Optional error response */
  errorResponse?: string;

  /** Any additional fields from the response */
  [key: string]: any;
}

/**
 * Main API middleware function
 *
 * Routes API requests based on environment and normalizes responses.
 *
 * @param payload - The API request payload
 * @returns Promise with normalized response
 *
 * @example
 * ```typescript
 * const response = await apiRequest({
 *   resourcePath: '/createShiftPlan',
 *   httpMethod: 'post',
 *   processName: 'shiftPlanManagementService',
 *   requestBody: { nodeId: 'LAS2', ofdDate: '2024-01-01' }
 * });
 * ```
 */
export async function apiRequest<T = any>(
  payload: ApiRequestPayload
): Promise<ApiResponse<T>> {
  const { resourcePath, httpMethod, processName, requestBody } = payload;

  // Determine environment and route accordingly
  const isLocal = isLocalEnvironment();

  console.log(isLocal);

  if (isLocal) {
    // Local environment: Use REST API via apiClient
    return handleRestRequest<T>(resourcePath, httpMethod, requestBody);
  } else {
    // Web/Embedded environment: Use parent window postMessage
    return handleParentWindowRequest<T>(payload);
  }
}

/**
 * Handle REST API request using apiClient (for local environment)
 * In local environment, routes specific endpoints to MockDataService
 *
 * @param resourcePath - API endpoint path
 * @param httpMethod - HTTP method
 * @param requestBody - Request body
 * @returns Promise with normalized response
 */
async function handleRestRequest<T = any>(
  resourcePath: string,
  httpMethod: string,
  requestBody?: any
): Promise<ApiResponse<T>> {
  try {
    const method = httpMethod.toLowerCase();
    let response: any;

    // Route specific endpoints to MockDataService in local environment
    const mockResponse = await tryMockDataService<T>(resourcePath, method, requestBody);
    if (mockResponse) {
      return mockResponse;
    }

    // Fall back to real API client for endpoints not mocked
    console.log(`[API Middleware] Using real API for ${method.toUpperCase()} ${resourcePath}`);
    
    // Map HTTP method to apiClient method
    switch (method) {
      case 'get':
        response = await apiClient.get<T>(resourcePath);
        break;
      case 'post':
        response = await apiClient.post<T>(resourcePath, requestBody);
        break;
      case 'put':
        response = await apiClient.put<T>(resourcePath, requestBody);
        break;
      case 'delete':
        response = await apiClient.delete<T>(resourcePath);
        break;
      case 'patch':
        response = await apiClient.patch<T>(resourcePath, requestBody);
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${httpMethod}`);
    }

    // Normalize response structure
    return {
      data: response,
      status: 200, // Axios successful responses are always 200-level
    };
  } catch (error: any) {
    // Handle errors and normalize error response
    console.error('[API Middleware] REST request failed:', error);

    // Enhance ValidationFailedException errors
    const enhancedError = enhanceValidationError(error);

    return {
      data: null as T,
      status: enhancedError.status || 500,
      errorResponse: enhancedError.message || 'Request failed',
    };
  }
}

/**
 * Try to handle request with MockDataService
 * Returns null if the endpoint is not mocked or if mocking is disabled
 */
async function tryMockDataService<T = any>(
  resourcePath: string,
  method: string,
  requestBody?: any
): Promise<ApiResponse<T> | null> {
  // Check if mocking is globally disabled
  const isMockDisabled = process.env.DISABLE_MOCK_DATA_SERVICE === 'true' ||
                         (global as any).__DISABLE_MOCK_DATA_SERVICE__ === true;
  
  if (isMockDisabled) {
    console.log(`[API Middleware] Mock service disabled globally, using real API for ${method.toUpperCase()} ${resourcePath}`);
    return null;
  }

  try {
    // Extract common parameters from request body for dynamic mock responses
    // Use defaults from dev config if not provided in request
    const mockParams = {
      warehouseId: requestBody?.warehouseId || getDefaultWarehouseId(),
      nodeId: requestBody?.nodeId || getDefaultNodeId(),
      planId: requestBody?.planId,
      ofdDate: requestBody?.ofdDate,
      ...requestBody // Include any other parameters
    };

    // Log parameter injection for debugging
    if (isLocalEnvironment() && (mockParams.warehouseId || mockParams.nodeId)) {
      console.log(`[API Middleware] Injecting parameters for ${resourcePath}:`, {
        warehouseId: mockParams.warehouseId,
        nodeId: mockParams.nodeId,
        source: requestBody?.warehouseId ? 'request' : 'dev config'
      });
    }

    // Route specific API endpoints to mock service methods
    switch (resourcePath) {
      case '/getRatesAndRoster':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.getRatesAndRoster(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getTargetAndWIPVolumes':
      case '/getTargetAndWIPVolume':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.getTargetAndWipVolume(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/verifyVolumes':
        if (method === 'post') {
          const data = await MockDataService.verifyVolumes();
          return { data: data as T, status: 200 };
        }
        break;

      case '/getShiftPlan':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.getShiftPlan(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getAllShiftPlans':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.getAllShiftPlans();
          return { data: { data: { shiftPlans: data } } as T, status: 200 };
        }
        break;

      case '/getShiftPlanOutput':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.getShiftPlanOutput(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getPlanOverrides':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.getPlanOverrides(requestBody?.planId);
          return { data: data as T, status: 200 };
        }
        break;

      case '/shift-plans/plan/status':
      case '/publishShiftPlan':
        if (method === 'put' || method === 'post') {
          const planId = requestBody?.planId;
          const shiftType = requestBody?.shiftType;
          const data = await MockDataService.publishShiftPlan(planId, shiftType);
          return { data: data as T, status: 200 };
        }
        break;

      case '/shift-plans/plan/input':
      case '/verifyVolume':
        if (method === 'put' || method === 'post') {
          const planId = requestBody?.planId;
          const inputOverrides = requestBody?.inputOverrides;
          const data = await MockDataService.updateShiftPlan(planId, inputOverrides);
          return { data: data as T, status: 200 };
        }
        break;

      case '/createShiftPlan':
        if (method === 'post') {
          const data = await MockDataService.createShiftPlan(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getShiftRoster':
        if (method === 'post') {
          const data = await MockDataService.getShiftRoster(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getPlannedHeadcount':
        if (method === 'post') {
          const data = await MockDataService.getPlannedHeadcount(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getStationAssignment':
        if (method === 'post') {
          const data = await MockDataService.getStationAssignment(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/isValidStationAssignmentOverride':
        if (method === 'post') {
          const data = await MockDataService.isValidStationAssignmentOverride(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/publishStationAssignment':
        if (method === 'post') {
          return { data: { success: true } as T, status: 200 };
        }
        break;

      case '/getInboundTrailerVolume':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.getInboundTrailerVolume();
          return { data: data as T, status: 200 };
        }
        break;

      case '/listYardTrailers':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.getInboundTrailerVolume();
          return { data: data as T, status: 200 };
        }
        break;

      case '/getIndirectStaffingConfig':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.getIndirectStaffingConfig(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/updateIndirectStaffingConfig':
        if (method === 'post') {
          const data = await MockDataService.updateIndirectStaffingConfig(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/listConfigDrafts':
        if (method === 'post') {
          const data = await MockDataService.listConfigDrafts(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/promoteConfig':
        if (method === 'post') {
          const data = await MockDataService.promoteConfig(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getFlowPathPercentageConfig':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.getFlowPathPercentageConfig(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/updateWarehouseOperationalConfig':
        if (method === 'post') {
          const data = await MockDataService.updateWarehouseOperationalConfig(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getWorkItemCounts':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.getWorkItemCounts(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getWorkItems':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.getWorkItems({
            processPaths: requestBody?.processPaths ?? [],
            pageSize: requestBody?.pageSize,
            nextToken: requestBody?.nextToken,
          });
          return { data: data as T, status: 200 };
        }
        break;

      case '/loadWorkItem':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.loadWorkItem(
            requestBody?.workItemId ?? '',
          );
           return { data: data as T, status: 200 };
        }
        break;
      case '/getFlowMonitoringData':
        if (method === 'post') {
          const data = await MockDataService.getFlowMonitoringData(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/updateWorkItemState':
        if (method === 'post' || method === 'get') {
          const data = await MockDataService.updateWorkItemState({
            workItemId: requestBody?.workItemId ?? '',
            status: requestBody?.status,
            operator: requestBody?.operator ?? '',
          });
          return { data: data as T, status: 200 };
        }
        break;
      case '/compareFlowMonitoringData':
        if (method === 'post') {
          const data = await MockDataService.compareFlowMonitoringData(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/saveEosWashReport':
        if (method === 'post') {
          const data = await MockDataService.saveEosWashReport(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/rcc-agent/query':
        if (method === 'post') {
          const data = await MockDataService.queryAgent(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/rcc-agent/explain':
        if (method === 'post') {
          const data = await MockDataService.queryShiftPlanAgent(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/isFeatureEnabled':
        if (method === 'post') {
          // Local dev: always enabled
          return { data: { featureEnabled: true, featureName: mockParams?.featureName || '' } as T, status: 200 };
        }
        break;

      case '/getSiteLayoutConfig':
        if (method === 'post') {
          const data = await MockDataService.getSiteLayoutConfig(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getAvailableProcessPaths':
        if (method === 'post') {
          const data = await MockDataService.getAvailableProcessPaths(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/saveSiteLayoutConfig':
        if (method === 'post') {
          const data = await MockDataService.saveSiteLayoutConfig(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getCertificateFunctionMappings':
        if (method === 'post') {
          const data = await MockDataService.getCertificateFunctionMappings(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/upsertCertificateFunctionMapping':
        if (method === 'post') {
          const data = await MockDataService.upsertCertificateFunctionMapping(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/deleteCertificateFunctionMapping':
        if (method === 'post') {
          const data = await MockDataService.deleteCertificateFunctionMapping(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/dryRunShiftPlan':
        if (method === 'post') {
          const data = await MockDataService.dryRunShiftPlan(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getLeaderboard':
        if (method === 'post') {
          const data = await MockDataService.getLeaderboard(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getImageQualitySiteRankings':
        if (method === 'post') {
          const data = mockImageQualitySiteRankings(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getImageQualityReport':
        if (method === 'post') {
          const data = mockImageQualityReport(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getAssociateImageQuality':
        if (method === 'post') {
          const data = mockAssociateImageQuality(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      case '/getEvidencePhotoLinks':
        if (method === 'post') {
          const data = mockEvidencePhotoLinks(mockParams);
          return { data: data as T, status: 200 };
        }
        break;

      default:
        // Endpoint not mocked, return null to use real API
        return null;
    }
  } catch (error: any) {
    console.error(`[API Middleware] Mock service error for ${resourcePath}:`, error);
    return {
      data: null as T,
      status: 500,
      errorResponse: error.message || 'Mock service error',
    };
  }

  return null;
}

/**
 * Handle parent window request using postMessage (for web/embedded environment)
 *
 * @param payload - Complete API request payload
 * @returns Promise with normalized response
 */
async function handleParentWindowRequest<T = any>(
  payload: ApiRequestPayload
): Promise<ApiResponse<T>> {
  try {
    const response = await getAPIGatewayDataFromParent({
      resourcePath: payload.resourcePath,
      httpMethod: payload.httpMethod,
      processName: payload.processName,
      requestBody: payload.requestBody,
    });

    // Response from parent window is already in the expected format
    return response as ApiResponse<T>;
  } catch (error: any) {
    // Handle errors from parent window communication
    console.error('[API Middleware] Parent window request failed:', error);

    // Enhance ValidationFailedException errors
    const enhancedError = enhanceValidationError(error);

    return {
      data: null as T,
      status: enhancedError.status || 500,
      errorResponse: enhancedError.message ||
        'Unable to proceed with the request. Please try again later.',
    };
  }
}
